import { queryOptions } from '@tanstack/react-query'
import type { Database, Json } from '@/types/supabase'
import { supabase } from '@/lib/supabase/client'

export type NodeCanvas = Database['public']['Tables']['node_canvases']['Row']

export interface NodeCanvasWithAuthor extends NodeCanvas {
  profiles: {
    id: string
    email: string
    full_name: string | null
    avatar_url: string | null
  } | null
}

/** List rows leave the graph document out; it is only read by the editor. */
export type NodeCanvasListItem = Omit<NodeCanvasWithAuthor, 'definition'>

const AUTHOR_SELECT = 'profiles!author(id, email, full_name, avatar_url)'

/** The row changed since the caller loaded it; the write was not applied. */
export class CanvasConflictError extends Error {
  constructor(canvasId: string) {
    super(`Node canvas ${canvasId} changed since it was loaded`)
    this.name = 'CanvasConflictError'
  }
}

function requireId(value: string, what: string) {
  if (!value || value.trim() === '') {
    throw new Error(`Invalid ${what}: cannot query node canvases without it`)
  }
}

export const nodeCanvas = {
  findOne: (canvasId: string) =>
    queryOptions({
      queryKey: ['node-canvas', canvasId],
      queryFn: async (): Promise<NodeCanvasWithAuthor | null> => {
        requireId(canvasId, 'canvasId')

        const { data, error } = await supabase
          .from('node_canvases')
          .select(`*, ${AUTHOR_SELECT}`)
          .eq('id', canvasId)
          .is('deleted_at', null)
          .maybeSingle()

        if (error) {
          throw new Error(`Failed to fetch node canvas: ${error.message}`, {
            cause: error,
          })
        }

        return data as NodeCanvasWithAuthor | null
      },
      // The open editor owns the graph; autosave writes back through setQueryData.
      staleTime: Infinity,
    }),

  findManyByWorkspace: (workspaceId: string) =>
    queryOptions({
      queryKey: ['node-canvases', 'workspace', workspaceId],
      queryFn: async (): Promise<NodeCanvasListItem[]> => {
        requireId(workspaceId, 'workspaceId')

        const { data, error } = await supabase
          .from('node_canvases')
          .select(
            `id, workspace_id, name, author, deleted_at, created_at, updated_at, ${AUTHOR_SELECT}`
          )
          .eq('workspace_id', workspaceId)
          .is('deleted_at', null)
          .order('created_at', { ascending: false })

        if (error) {
          throw new Error(`Failed to fetch node canvases: ${error.message}`, {
            cause: error,
          })
        }

        return (data ?? []) as NodeCanvasListItem[]
      },
    }),

  create: async (input: {
    workspaceId: string
    author: string
    name?: string
    definition?: NonNullable<Json>
  }): Promise<NodeCanvas> => {
    requireId(input.workspaceId, 'workspaceId')

    const { data, error } = await supabase
      .from('node_canvases')
      .insert({
        workspace_id: input.workspaceId,
        author: input.author,
        ...(input.name ? { name: input.name } : {}),
        ...(input.definition !== undefined
          ? { definition: input.definition }
          : {}),
      })
      .select('*')
      .single()

    if (error) {
      throw new Error(`Failed to create node canvas: ${error.message}`, {
        cause: error,
      })
    }

    return data
  },

  /**
   * With `ifUpdatedAt`, the write applies only while the row still carries
   * that timestamp and a mismatch throws CanvasConflictError, so two editors
   * cannot silently overwrite each other.
   */
  update: async (
    canvasId: string,
    input: { name?: string; definition?: NonNullable<Json> },
    options: { ifUpdatedAt?: string } = {}
  ): Promise<NodeCanvas> => {
    requireId(canvasId, 'canvasId')

    let query = supabase
      .from('node_canvases')
      .update({
        ...(input.name !== undefined ? { name: input.name } : {}),
        ...(input.definition !== undefined
          ? { definition: input.definition }
          : {}),
      })
      .eq('id', canvasId)
    if (options.ifUpdatedAt) {
      query = query.eq('updated_at', options.ifUpdatedAt)
    }
    const { data, error } = await query.select('*').maybeSingle()

    if (error) {
      throw new Error(`Failed to update node canvas: ${error.message}`, {
        cause: error,
      })
    }
    if (!data) {
      throw options.ifUpdatedAt
        ? new CanvasConflictError(canvasId)
        : new Error('Failed to update node canvas: not found')
    }

    return data
  },

  /**
   * Writes some nodes' run state beside the document: an object replaces a
   * node's entry, null removes it. Unconditional and never an edit, so any
   * viewer may write what it observed without touching the save check.
   */
  mergeNodeRuns: async (
    canvasId: string,
    runs: Record<string, Record<string, unknown> | null>
  ): Promise<void> => {
    requireId(canvasId, 'canvasId')

    const { error } = await supabase.rpc('merge_node_runs', {
      p_canvas_id: canvasId,
      p_runs: runs as unknown as Json,
    })

    if (error) {
      throw new Error(`Failed to save node run state: ${error.message}`, {
        cause: error,
      })
    }
  },

  delete: async (canvasId: string): Promise<void> => {
    requireId(canvasId, 'canvasId')

    const { error } = await supabase
      .from('node_canvases')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', canvasId)

    if (error) {
      throw new Error(`Failed to delete node canvas: ${error.message}`, {
        cause: error,
      })
    }
  },
}
